import { Suspense, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, ScrollView } from 'react-native';
import {
  Flex,
  PrimaryButton,
  StackScreen,
  Typography,
  useSafeArea,
  useScreenSize,
} from '@banhala/ui-kit';
import { useNavigation } from '@react-navigation/native';
import { useQueryClient } from '@tanstack/react-query';
import {
  useHandleBackPressed,
  useInstanceId,
  useReportTTFD,
} from 'shared/src/hooks';
import { useRoute } from 'shared/src/hooks/useRoute';
import { AnalyticsScreen } from 'shared/src/context';
import { bridge } from 'shared/src/utils';
import { systemAlert } from 'shared/src/component/systemAlert';
import type { Address } from 'mypage/src/api/addressService';
import { closeAddressFlow } from 'mypage/src/utils/closeAddressFlow';
import { queryKey } from '../../constant/queryKey';
import { useGetAddresses } from './queries/useGetAddresses';
import { useChangeDefaultAddress } from './queries/useChangeDefaultAddress';
import { useDeleteAddress } from './queries/useDeleteAddress';
import { useLogViewAddress } from './hooks/useLogViewAddress';
import { useLogCompleteChangeShippingAddress } from './hooks/useLogCompleteChangeShippingAddress';
import { AddressItem } from './components/AddressItem';
import type { AddressStackParamList } from './AddressStackParamList';

type AddressMode = 'manage' | 'select';

type ListNavigation = {
  navigate: (
    route:
      | { name: 'Editor'; params: AddressStackParamList['Editor']; merge?: boolean }
      | {
          name: 'AddressList';
          params: AddressStackParamList['AddressList'];
          merge?: boolean;
        },
  ) => void;
  setParams: (
    params: Partial<AddressStackParamList['AddressList']>,
  ) => void;
  replace: (name: 'Editor', params: AddressStackParamList['Editor']) => void;
};

const AddressListScreen = () => {
  const { showBackButton, onPressBack } = useHandleBackPressed();
  const { width } = useScreenSize();
  const { params } = useRoute<AddressStackParamList, 'AddressList'>();
  const mode: AddressMode = params?.mode === 'select' ? 'select' : 'manage';

  useLogViewAddress();
  useReportTTFD();

  return (
    <AnalyticsScreen screen="ADDRESS">
      <StackScreen
        title="배송지 변경"
        hideBackButton={!showBackButton}
        onPressBack={onPressBack}
        rightIcons={[<AddressAddButton key="add-address" />]}
      >
        {/* QA 자동화 화면 식별자(ENABLE-1174) — 수정 화면과 구분 위해 화면 컨테이너에 부여 */}
        <Flex flex={1} testID="배송지변경">
          <Suspense
            fallback={
              <Flex flex={1} width={width} margin={{ horizontal: 'auto' }}>
                <Flex.Center flex={1}>
                  <ActivityIndicator />
                </Flex.Center>
              </Flex>
            }
          >
            <AddressListBody mode={mode} />
          </Suspense>
        </Flex>
      </StackScreen>
    </AnalyticsScreen>
  );
};

const AddressAddButton = () => {
  const navigation = useNavigation<ListNavigation>();
  return (
    <Flex
      testID="배송지추가버튼"
      onPress={() => navigation.navigate({ name: 'Editor', params: {} })}
      padding={{ horizontal: 12, vertical: 8 }}
    >
      <Typography type="body1" color="content_blue_primary">
        배송지 추가
      </Typography>
    </Flex>
  );
};

/**
 * Suspense 경계 내부 컴포넌트.
 * - 단일 컴포넌트에서 selectedSno state + 변경 완료 액션 공유.
 * - 기본 배송지를 첫 진입 시 selected로 초기화 (iOS `selectedSno = addresses.first { isDefault }` 패턴).
 * - 정렬: 기본 배송지 최상단, 단 이 화면에서 신규 추가한 배송지가 그보다 위 (iOS/Android 동일).
 */
const AddressListBody = ({ mode }: { mode: AddressMode }) => {
  const { width } = useScreenSize();
  const insets = useSafeArea();
  const instanceId = useInstanceId();
  const navigation = useNavigation<ListNavigation>();
  const { params } = useRoute<AddressStackParamList, 'AddressList'>();

  const { data } = useGetAddresses();
  const { mutateAsync: changeDefault, isPending } = useChangeDefaultAddress();
  const { mutate: deleteAddress, isPending: isDeleting } = useDeleteAddress();
  const logComplete = useLogCompleteChangeShippingAddress();

  const defaultSno = useMemo(
    () => data.addresses.find(a => a.is_default_address)?.sno,
    [data.addresses],
  );

  const [selectedSno, setSelectedSno] = useState<number | undefined>(
    defaultSno,
  );

  // ENABLE-893: stale 캐시로 첫 렌더된 뒤 refetch가 기본배송지를 뒤늦게 내려주는 케이스.
  // useState 초기값은 첫 렌더에 고정되므로, 선택이 없을 때만 기본배송지로 동기화.
  useEffect(() => {
    if (selectedSno === undefined && defaultSno !== undefined) {
      setSelectedSno(defaultSno);
    }
  }, [defaultSno, selectedSno]);

  // ENABLE-888: 이 화면에서 신규 추가된 배송지 sno (최신순).
  // 서버 재조회(invalidate) 순서와 무관하게 최상단 정렬 유지 (iOS `insert(at: 0)` 패리티)
  const [newSnos, setNewSnos] = useState<number[]>([]);

  const sortedAddresses = useMemo(() => {
    const added = newSnos
      .map(sno => data.addresses.find(a => a.sno === sno))
      .filter((a): a is Address => a !== undefined);
    const rest = data.addresses
      .filter(a => !newSnos.includes(a.sno))
      .sort(
        (a, b) => Number(b.is_default_address) - Number(a.is_default_address),
      );
    return [...added, ...rest];
  }, [data.addresses, newSnos]);

  const isEditedDefault =
    selectedSno !== undefined && selectedSno !== defaultSno;

  // Editor(ENABLE-581) 저장 결과 — navigation params(savedSno)로 전달받아 선택 상태 반영
  useEffect(() => {
    if (params?.savedSno === undefined) return;
    const savedSno = params.savedSno;
    setSelectedSno(savedSno);
    // ENABLE-888: 신규 추가는 최상단 정렬 대상으로 등록
    if (params.savedIsNew === true) {
      setNewSnos(prev => [savedSno, ...prev.filter(sno => sno !== savedSno)]);
    }
    navigation.setParams({ savedSno: undefined, savedIsNew: undefined });
  }, [navigation, params?.savedSno, params?.savedIsNew]);

  // ENABLE-602: 첫 진입 시 목록이 비어있으면 추가 화면으로 자동 진입 (iOS 동일)
  useEffect(() => {
    if (data.addresses.length === 0) {
      navigation.replace('Editor', { shouldSetDefaultAddress: true });
    }
    // 마운트 1회만 검사 — 추가 후 List 복귀나 swipe back 케이스에선 자동 진입하지 않음
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePressSubmit = async () => {
    if (isPending) return;

    const selected = data.addresses.find(a => a.sno === selectedSno);
    if (!selected) {
      closeAddressFlow(instanceId);
      return;
    }

    if (mode === 'select') {
      // 기본배송지 설정 API는 변경 있을 때만 (iOS changeAddress 패턴)
      if (isEditedDefault) {
        await changeDefault({ default_address_sno: selected.sno });
      }
      // ENABLE-995: 완료 버튼 클릭 시 변경 여부와 무관하게 항상 발사 (iOS 패리티)
      logComplete(selected);
      // 결제 WebView 진입 — native owner의 onResult 콜백 발화 (ENABLE-579 추상화)
      bridge.deliverScreenResult({
        instanceId,
        targetInstanceId: instanceId,
        result: selected as unknown as Record<string, unknown>,
      });
      closeAddressFlow(instanceId);
      return;
    }

    // manage 모드 — 기본배송지 설정 API는 변경 감지 시에만 (iOS `isEditedDefault` 패턴)
    if (isEditedDefault) {
      await changeDefault({ default_address_sno: selected.sno });
    }
    // ENABLE-995: 완료 버튼 클릭 시 변경 여부와 무관하게 항상 발사 (iOS 패리티)
    logComplete(selected);
    closeAddressFlow(instanceId);
  };

  // iOS `deleteConfirmAlert` + `setDeleteAddress` 패턴.
  // 선택된 배송지를 삭제하면 남은 첫 항목이 새 선택(=기본 후보)이 됨.
  const handlePressDelete = (sno: number) => {
    systemAlert('', '배송지를 삭제하시겠어요?', [
      { text: '취소', style: 'default' },
      {
        text: '삭제',
        style: 'destructive',
        onPress: () => {
          deleteAddress(
            { sno },
            {
              onSuccess: () => {
                bridge.toast('배송지가 삭제되었습니다.');
                // ENABLE-602: 마지막 배송지를 삭제하면 화면 종료 (iOS 동일)
                if (data.addresses.length <= 1) {
                  closeAddressFlow(instanceId);
                  return;
                }
                if (selectedSno === sno) {
                  setSelectedSno(sortedAddresses.find(a => a.sno !== sno)?.sno);
                }
              },
              onError: () => {
                bridge.toast('배송지를 삭제하지 못했습니다.');
              },
            },
          );
        },
      },
    ]);
  };

  // iOS `addressEditPublisher` 패턴 — 수정 모드로 Editor 진입.
  // 기본 배송지를 수정하는 경우 shouldSetDefaultAddress=true (iOS 동일).
  const handlePressEdit = (addr: Address) => {
    navigation.navigate({
      name: 'Editor',
      params: {
        addressSno: addr.sno,
        name: addr.name,
        zipcode: addr.zipcode,
        address: addr.address,
        addressDetail: addr.address_detail,
        mobile: addr.mobile,
        shouldSetDefaultAddress: addr.sno === defaultSno,
      },
    });
  };

  return (
    <Flex flex={1}>
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{
          width,
          marginHorizontal: 'auto',
          paddingHorizontal: 16,
          paddingTop: 16,
          paddingBottom: 16,
        }}
        showsVerticalScrollIndicator={false}
      >
        <Flex gap={16}>
          {sortedAddresses.map((address, index) => (
            <AddressItem
              key={address.sno}
              index={index}
              address={address}
              isSelected={selectedSno === address.sno}
              onPress={() => setSelectedSno(address.sno)}
              onPressEdit={() => handlePressEdit(address)}
              onPressDelete={() => handlePressDelete(address.sno)}
            />
          ))}
        </Flex>
      </ScrollView>
      <Flex
        borderTopWidth={1}
        borderColor="gray20"
        padding={{
          top: 16,
          horizontal: 16,
          bottom: insets.bottom + 16,
        }}
      >
        <PrimaryButton
          testID="배송지변경완료버튼"
          variant="fill"
          color="pink"
          size="xlarge"
          onPress={handlePressSubmit}
        >
          배송지 변경 완료
        </PrimaryButton>
      </Flex>
      {isDeleting && (
        <Flex
          position="absolute"
          top={0}
          bottom={0}
          left={0}
          right={0}
          zIndex={1}
          backgroundColor="alpha_dark30"
          justifyContent="center"
          alignItems="center"
        >
          <ActivityIndicator color="white" />
        </Flex>
      )}
    </Flex>
  );
};

// QueryClient 캐시 갱신은 Editor가 setQueryData로 직접 처리 (이전 useNativeEvent 경로 대체)
// ENABLE-895: 키가 인스턴스별이므로 호출자(Editor)의 instanceId를 받아 같은 캐시를 갱신
export const updateAddressesCache = (
  client: ReturnType<typeof useQueryClient>,
  instanceId: string,
  saved: Address,
) => {
  client.setQueryData<{ addresses: Address[] }>(
    queryKey.addresses(instanceId),
    old => {
      if (!old) return old;
      const exists = old.addresses.some(a => a.sno === saved.sno);
      return {
        addresses: exists
          ? old.addresses.map(a => (a.sno === saved.sno ? saved : a))
          : [saved, ...old.addresses],
      };
    },
  );
};

export default AddressListScreen;
